import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  useTheme,
  alpha,
  InputBase,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch, useAuth } from '@/hooks';
import { toggleThemeMode, toggleSidebar, setFullscreen } from '@/store/uiSlice';

interface HeaderProps {
  title?: string;
  showSearch?: boolean;
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  title, 
  showSearch = true,
  onMenuClick 
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { logout, user } = useAuth();
  const { themeMode, isFullscreen, sidebarCollapsed } = useAppSelector((state) => state.ui);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      dispatch(setFullscreen(true));
    } else {
      document.exitFullscreen();
      dispatch(setFullscreen(false));
    }
  };

  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
    } else {
      dispatch(toggleSidebar());
    }
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
      }}
    >
      <Toolbar sx={{ gap: 2 }}>
        {/* Menú hamburguesa */}
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleMenuClick}
          sx={{ 
            display: { xs: 'flex', md: sidebarCollapsed ? 'flex' : 'none' } 
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Título */}
        {title && (
          <Typography
            variant="h6"
            component="h1"
            fontWeight={600}
            sx={{ display: { xs: 'none', sm: 'block' } }}
          >
            {title}
          </Typography>
        )}

        {/* Búsqueda global */}
        {showSearch && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: 2,
              px: 2,
              py: 0.5,
              flex: 1,
              maxWidth: 400,
              ml: 2,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              },
              '&:focus-within': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
            }}
          >
            <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
            <InputBase
              placeholder="Buscar productos, clientes, ventas..."
              sx={{
                flex: 1,
                fontSize: '0.875rem',
                '& input': {
                  p: 0,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                backgroundColor: alpha(theme.palette.common.black, 0.08),
                px: 1,
                py: 0.25,
                borderRadius: 1,
                ml: 1,
              }}
            >
              Ctrl+K
            </Typography>
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Acciones */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Pantalla completa */}
          <Tooltip title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}>
            <IconButton onClick={handleToggleFullscreen} color="inherit">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>

          {/* Tema claro/oscuro */}
          <Tooltip title={themeMode === 'light' ? 'Modo oscuro' : 'Modo claro'}>
            <IconButton
              onClick={() => dispatch(toggleThemeMode())}
              color="inherit"
            >
              {themeMode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Notificaciones */}
          <Tooltip title="Notificaciones">
            <IconButton onClick={handleNotificationOpen} color="inherit">
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Avatar del usuario */}
          <Tooltip title="Mi cuenta">
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{
                ml: 1,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                p: 0.25,
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {user?.nombre?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Menú de perfil */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: 2,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {user?.nombreCompleto || 'Usuario'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email || ''}
            </Typography>
          </Box>
          <Divider />
          <MenuItem>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mi perfil</ListItemText>
          </MenuItem>
          <MenuItem>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Configuración</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={logout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography color="error">Cerrar sesión</Typography>
            </ListItemText>
          </MenuItem>
        </Menu>

        {/* Menú de notificaciones */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 320,
              maxWidth: 400,
              borderRadius: 2,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Notificaciones
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Marcar todas como leídas
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No hay notificaciones nuevas
            </Typography>
          </Box>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
